import { useState, useEffect } from "react";
import { Plus, Pencil, Trash, X, Check, Lock } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` } });

  const handleAuthError = (error) => {
    if (error.response?.status === 401) handleLogout();
  };

  useEffect(() => {
    if (!sessionStorage.getItem("adminToken")) return;
    axios.get(`${API}/admin/me`, authHeaders())
      .then(() => setIsAuthenticated(true))
      .catch(() => sessionStorage.removeItem("adminToken"));
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError("");

    try {
      const { data } = await axios.post(`${API}/admin/login`, loginData);
      sessionStorage.setItem("adminToken", data.token);
      setIsAuthenticated(true);
    } catch (error) {
      setLoginError(error.response?.status === 429 ? "Too many failed attempts. Please try again in 15 minutes." : "Invalid username or password");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    setIsAuthenticated(false);
    setLoginData({ username: "", password: "" });
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ title: "", content: "", excerpt: "" });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.id}`, formData, authHeaders());
      } else {
        await axios.post(`${API}/posts`, formData, authHeaders());
      }
      fetchPosts();
      resetForm();
    } catch (error) {
      handleAuthError(error);
      console.error("Error saving post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || ""
    });
    setEditingPost(post);
    setShowForm(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      await axios.delete(`${API}/posts/${postId}`, authHeaders());
      fetchPosts();
    } catch (error) {
      handleAuthError(error);
      console.error("Error deleting post:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="admin-login">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#1565C0] flex items-center justify-center mx-auto mb-6">
              <Lock size={32} weight="bold" className="text-white" />
            </div>
            <h1 className="font-['Cabinet_Grotesk',sans-serif] text-3xl font-bold text-[#0D1B2A]">
              Admin Login
            </h1>
            <p className="mt-2 text-[#607D8B]">Enter your credentials to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            {loginError && (
              <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" data-testid="login-error">
                {loginError}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-bold text-[#0D1B2A] mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                required
                className="w-full border-2 border-[#E0E7ED] px-4 py-3 text-[#0D1B2A] focus:border-[#1565C0] focus:outline-none transition-colors"
                placeholder="Enter username"
                data-testid="username-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-[#0D1B2A] mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                className="w-full border-2 border-[#E0E7ED] px-4 py-3 text-[#0D1B2A] focus:border-[#1565C0] focus:outline-none transition-colors"
                placeholder="Enter password"
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full btn-premium inline-flex items-center justify-center px-6 py-4 text-sm font-bold tracking-wide bg-[#1565C0] text-white hover:bg-[#0D47A1] transition-all disabled:opacity-50"
              data-testid="login-button"
            >
              {isLoggingIn ? "Logging in..." : "Login"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div data-testid="admin-page">
      {/* Header */}
      <section className="py-12 border-b border-[#E0E7ED]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            <div>
              <div className="accent-bar mb-4"></div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1565C0] mb-2">Admin</p>
              <h1 className="font-['Cabinet_Grotesk',sans-serif] text-2xl md:text-3xl font-bold text-[#0D1B2A] tracking-tight" data-testid="admin-heading">
                Manage Posts
              </h1>
            </div>
            <div className="flex items-center gap-4">
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="btn-premium inline-flex items-center gap-2 px-6 py-4 text-sm font-bold tracking-wide bg-[#1565C0] text-white hover:bg-[#0D47A1] transition-all"
                  data-testid="new-post-button"
                >
                  <Plus size={18} weight="bold" /> New Post
                </button>
              )}
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-[#607D8B] hover:text-[#0D1B2A] transition-colors"
                data-testid="logout-button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.section 
            className="py-10 bg-[#F0F4F8] border-b border-[#E0E7ED]"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            data-testid="post-form-section"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-['Cabinet_Grotesk',sans-serif] text-xl font-bold text-[#0D1B2A]">
                  {editingPost ? "Edit Post" : "New Post"}
                </h2>
                <button
                  onClick={resetForm}
                  className="p-2 text-[#607D8B] hover:text-[#0D1B2A] transition-colors"
                  data-testid="close-form-button"
                >
                  <X size={24} weight="bold" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8" data-testid="post-form">
                <div>
                  <label htmlFor="title" className="block text-xs font-bold tracking-[0.2em] uppercase text-[#607D8B] mb-3">
                    Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full border-2 border-[#E0E7ED] bg-white px-5 py-4 text-[#0D1B2A] focus:border-[#1565C0] focus:outline-none transition-colors"
                    placeholder="Post title"
                    data-testid="title-input"
                  />
                </div>

                <div>
                  <label htmlFor="excerpt" className="block text-xs font-bold tracking-[0.2em] uppercase text-[#607D8B] mb-3">
                    Excerpt (optional)
                  </label>
                  <input
                    type="text"
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    className="w-full border-2 border-[#E0E7ED] bg-white px-5 py-4 text-[#0D1B2A] focus:border-[#1565C0] focus:outline-none transition-colors"
                    placeholder="Short summary for the post list"
                    data-testid="excerpt-input"
                  />
                </div>

                <div>
                  <label htmlFor="content" className="block text-xs font-bold tracking-[0.2em] uppercase text-[#607D8B] mb-3">
                    Content
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    required
                    rows={12}
                    className="w-full border-2 border-[#E0E7ED] bg-white px-5 py-4 text-[#0D1B2A] focus:border-[#1565C0] focus:outline-none resize-none transition-colors"
                    placeholder="Write your post content here..."
                    data-testid="content-input"
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-premium inline-flex items-center gap-2 px-8 py-4 text-sm font-bold tracking-wide bg-[#1565C0] text-white hover:bg-[#0D47A1] transition-all disabled:opacity-50"
                    data-testid="save-post-button"
                  >
                    <Check size={18} weight="bold" /> {isSubmitting ? "Saving..." : "Save Post"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-8 py-4 text-sm font-bold tracking-wide border-2 border-[#E0E7ED] text-[#607D8B] hover:border-[#0D1B2A] hover:text-[#0D1B2A] transition-all"
                    data-testid="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Posts List */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="py-16 text-center text-[#607D8B]" data-testid="admin-loading">
              Loading posts...
            </div>
          ) : posts.length === 0 ? (
            <div className="py-16 text-center text-[#607D8B]" data-testid="admin-empty">
              No posts yet. Create your first post!
            </div>
          ) : (
            <div className="border-t-2 border-[#E0E7ED]" data-testid="admin-posts-list">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  className="flex items-center justify-between py-6 border-b-2 border-[#E0E7ED] group"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`admin-post-${index}`}
                >
                  <div className="flex-1 min-w-0 pr-6">
                    <h3 className="font-['Cabinet_Grotesk',sans-serif] text-lg font-bold text-[#0D1B2A] truncate group-hover:text-[#1565C0] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-[#607D8B] mt-1">
                      {formatDate(post.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(post)}
                      className="p-3 text-[#607D8B] hover:text-[#1565C0] hover:bg-[#F0F4F8] transition-all"
                      title="Edit post"
                      data-testid={`edit-post-${index}`}
                    >
                      <Pencil size={18} weight="bold" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-3 text-[#607D8B] hover:text-red-600 hover:bg-red-50 transition-all"
                      title="Delete post"
                      data-testid={`delete-post-${index}`}
                    >
                      <Trash size={18} weight="bold" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
